import React, {
	createContext,
	useContext,
	useState,
} from 'react';
import {
	useVisualContentLoop,
} from './hooks/visualContentLoop/useVisualContentLoop';
import CustomIcon from '../Common/CustomIcon/CustomIcon';

// Create the context
const VisualContentLoopContext = createContext(null);

// Global setter for the visual content loop
let globalSetVisualContentLoop = null;

// Provider component that manages the shared state
export const VisualContentLoopProvider = ({
	children,
	isInFolderSchedulingEnabled = false,
}) => {
	const [
		visualContentLoop,
		setVisualContentLoop,
	] = useState(null);

	// Store global reference for external access
	globalSetVisualContentLoop = setVisualContentLoop;

	const hookData = useVisualContentLoop(visualContentLoop, isInFolderSchedulingEnabled);

	return (
		<VisualContentLoopContext.Provider value={hookData}>
			{children}
		</VisualContentLoopContext.Provider>
	);
};

// Function to set the visual content loop from outside the provider
export const setGlobalVisualContentLoop = (visualContentLoop) => {
	if (globalSetVisualContentLoop) {
		globalSetVisualContentLoop(visualContentLoop);
	}
};

// Custom hook to use the context
export const useVisualContentLoopContext = () => {
	const context = useContext(VisualContentLoopContext);
	if (!context) {
		throw new Error('useVisualContentLoopContext must be used within a VisualContentLoopProvider');
	}
	return context;
};

// SaveButton component that uses the shared context
export const SaveButton = ({ location, }) => {
	const {
		handleSave,
		isSaving,
		isRefreshing,
		hasChanges,
		canSave,
		isBaselineLoaded,
	} = useVisualContentLoopContext();

	const isDisabled = isSaving || isRefreshing || !canSave;

	// Determine button title based on state
	let buttonTitle = 'Save changes';
	if (isSaving) {
		buttonTitle = 'Saving...';
	} else if (isRefreshing) {
		buttonTitle = 'Refreshing...';
	} else if (hasChanges && !isBaselineLoaded) {
		// Saving replaces the loop's entire provider list, so it is not safe until we
		// have read the loop from the server at least once.
		buttonTitle = 'This loop could not be loaded — use Refresh before saving';
	} else if (!hasChanges) {
		buttonTitle = 'No changes to save';
	}

	return (
		<button
			className={`button ${isDisabled ? 'button-disabled' : ''}`}
			onClick={() => {
				handleSave().catch(() => {
					alert('Error saving changes. Please try again.');
				});
			}}
			disabled={isDisabled}
			data-test-id={`visualContentLoopSaveButton-${location}`}
			title={buttonTitle}
			style={{
				display:    'flex',
				alignItems: 'center',
				gap:        '5px',
			}}
		>
			<CustomIcon icon="save" />
			SAVE
		</button>
	);
};